import { useState, useEffect, useCallback } from 'react';
import { Modal, Box, Text, Paper, Stack, Loader } from '@mantine/core';
import { apiInstance } from '../../services/api_service';

const StudentLogs = ({ 
  opened, 
  onClose, 
  studentId, 
  studentName, 
  context 
}) => {
  const [isLoadingLogs, setIsLoadingLogs] = useState(false);
  const [logs, setLogs] = useState([]);

  // Centralized source translation mapping
  const sourceTranslations = {
    'email': 'Email',
    'phonecall': 'Ligação telefônica',
    'in_person': 'Presencial',
    'meeting': 'Reunião',
    'tellme': "Tellme",
    'other': 'Outro'
  };

  const fetchLogs = useCallback(async () => {
    if (!studentId || !context) return;
    
    setIsLoadingLogs(true);
    try {
      const response = await apiInstance.get(`/students/${studentId}/log/?context=${context}`);
      setLogs(response.data.results);
    } catch (error) {
      console.error('Error fetching logs:', error);
      setLogs([]);
    } finally {
      setIsLoadingLogs(false);
    }
  }, [studentId, context]);

  useEffect(() => {
    if (opened) {
      fetchLogs();
    }
  }, [opened, fetchLogs]);

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={
        <Text weight={600} size="xl" color="#1a1b1e">
          Comentários - {studentName}
        </Text>
      }
      size="70%"
      centered
      styles={{
        modal: {
          height: '80vh',
          maxHeight: '80vh',
        },
        header: {
          backgroundColor: '#f8f9fa',
          borderBottom: '1px solid #e9ecef',
          padding: '16px 24px',
        },
        body: {
          padding: 0,
          height: 'calc(80vh - 80px)',
          overflow: 'hidden',
        },
        content: {
          height: '80vh',
          maxHeight: '80vh',
        }
      }}
    >
      <Box 
        sx={{
          width: '100%',
          height: '100%',
          padding: '20px',
          backgroundColor: '#f8f9fa',
          overflow: 'auto',
        }}
      >
        <Paper 
          p="lg" 
          radius="md" 
          shadow="sm"
          sx={{
            backgroundColor: 'white',
            height: '100%',
            overflow: 'auto',
          }}
        >
          {isLoadingLogs ? (
            <Box 
              sx={{ 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center', 
                height: '200px' 
              }}
            >
              <Loader size="md" />
            </Box>
          ) : logs.length > 0 ? (
            <Box>
              {/* Header */}
              <Box 
                sx={{
                  borderBottom: '2px solid #e9ecef',
                  paddingBottom: '12px',
                  marginBottom: '16px',
                }}
              >
                <Text weight={600} size="lg" color="#1a1b1e">
                  Histórico de Comentários
                </Text>
                <Text size="sm" color="dimmed">
                  {logs.length} comentário{logs.length !== 1 ? 's' : ''} encontrado{logs.length !== 1 ? 's' : ''}
                </Text>
              </Box>

              {/* Logs List */}
              <Stack spacing="md">
                {logs.map((log, index) => (
                  <Paper 
                    key={index}
                    p="md" 
                    radius="md"
                    withBorder
                    sx={{
                      backgroundColor: '#fafbfc',
                      border: '1px solid #e9ecef',
                    }}
                  >
                    <Stack spacing="sm">
                      {/* Comment Content */}
                      <Text size="md" sx={{ lineHeight: 1.5 }}>
                        {log.reason}
                      </Text>
                      
                      {/* Metadata */}
                      <Box 
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          paddingTop: '8px',
                          borderTop: '1px solid #e9ecef',
                          '@media (max-width: 768px)': {
                            flexDirection: 'column',
                            alignItems: 'flex-start',
                            gap: '8px',
                          }
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                          <Text size="sm" color="dimmed" weight={500}>
                            👤 {log.created_by_user.first_name} {log.created_by_user.last_name}
                          </Text>
                          {log.source && (
                            <Text size="sm" color="blue" weight={500}>
                              📞 {sourceTranslations[log.source] || log.source}
                            </Text>
                          )}
                        </Box>
                        <Text size="sm" color="dimmed">
                          🕒 {new Date(log.created_at).toLocaleString('pt-BR')}
                        </Text>
                      </Box>
                    </Stack>
                  </Paper>
                ))}
              </Stack>
            </Box>
          ) : (
            <Box 
              sx={{ 
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center', 
                justifyContent: 'center',
                height: '300px',
                textAlign: 'center',
              }}
            >
              <Text size="xl" color="dimmed" mb="sm">
                💬
              </Text>
              <Text size="lg" color="dimmed" weight={500} mb="xs">
                Nenhum comentário encontrado
              </Text>
              <Text size="sm" color="dimmed">
                Este aluno ainda não possui comentários registrados.
              </Text>
            </Box>
          )}
        </Paper>
      </Box>
    </Modal>
  );
};

export default StudentLogs;
